<template>
  <div class="yoyo-container mx-auto min-h-screen">
    <!-- 头部导航 -->
    <navBar :title="isEdit ? $t('address.editAddress') : $t('address.addAddress')"></navBar>

    <!-- 表单内容 -->
    <div class="bg-white rounded-lg mx-4 mt-3">
      <van-field v-model="form.name" :label="$t('address.name')" :placeholder="$t('address.namePlaceholder')" />
      <van-field :label="$t('address.phone')" :placeholder="$t('address.phonePlaceholder')">
        <template #input>
          <div class="flex-1 flex items-center justify-end">
            <div>
              <van-field
                v-model="form.countryCode"
                :placeholder="$t('address.countryCodePlaceholder')"
                class="p-0"
                readonly
                is-link
                @click-input="showCountryCodePopup = true" />
            </div>
            <van-field v-model="form.mobile" :placeholder="$t('address.phonePlaceholder')" class="p-0 w-40 bg-orange-500" />
          </div>
        </template>
      </van-field>
      <van-field
        v-model="form.country"
        :label="$t('address.country')"
        :placeholder="$t('address.countryPlaceholder')"
        readonly
        is-link
        @click-input="handleCountry" />
      <van-field
        v-model="form.city"
        :label="$t('address.city')"
        :placeholder="$t('address.cityPlaceholder')"
        readonly
        is-link
        @click-input="showCityPopup = true" />
      <van-field
        v-model="form.zipCode"
        :label="$t('address.zipCode')"
        label-width="100px"
        :placeholder="$t('address.zipCodePlaceholder')" />

      <!-- 详细地址 -->
      <van-field
        v-model="form.detailAddress"
        :label="$t('address.detailAddress')"
        label-width="100px"
        :placeholder="$t('address.detailAddressPlaceholder')"
        type="textarea" />
    </div>

    <!-- 设为默认收货地址 -->
    <div class="bg-white rounded-lg mx-4 mt-2 p-3 flex items-center justify-between">
      <span class="text-[#646566] text-sm">{{ $t("address.setAsDefault") }}</span>
      <van-switch v-model="form.defaultStatus" size="20px" active-color="#ff7a00" />
    </div>
    <van-button type="primary" round @click="handleSave" class="mx-16 w-[calc(100%-8rem)] mb-4 mt-10 bg-orange-500 border-orange-500">
      {{ $t("address.save") }}
    </van-button>

    <van-button
      v-if="isEdit"
      plain
      round
      type="danger"
      @click="handleDelete"
      class="mx-16 w-[calc(100%-8rem)] mb-3 text-[#ff7a00] border-orange-500">
      {{ $t("address.delete") }}
    </van-button>

    <!-- 国家选择弹窗 -->
    <van-popup v-model="showCountryPopup" position="bottom">
      <van-picker show-toolbar :columns="countryList" @confirm="onCountryConfirm" @cancel="showCountryPopup = false" />
    </van-popup>

    <!-- 城市选择弹窗 -->
    <van-popup v-model="showCityPopup" position="bottom">
      <van-picker show-toolbar :columns="cityList" @confirm="onCityConfirm" @cancel="showCityPopup = false" />
    </van-popup>

    <!-- 区号弹窗 -->
    <van-popup v-model="showCountryCodePopup" position="bottom">
      <van-picker show-toolbar :columns="countryCodeList" @confirm="onCountryCodeConfirm" @cancel="showCountryCodePopup = false">
        <template v-slot:option="slotProps">
          <div class="flex items-center text-sm">
            <img :src="slotProps.icon" class="w-5 h-auto" alt="" />
            <span class="ml-3">{{ slotProps.text }}</span>
          </div>
        </template>
      </van-picker>
    </van-popup>
  </div>
</template>

<script>
import { Field, Button, Switch, Popup, Toast, Dialog, Picker } from "vant"
import { addAddress, updateAddress, deleteAddress, getAddressDetail } from "@/apis/userInfo"
import { getAddressCountryList, getCityList } from "@/apis/shop"
import { mapGetters } from "vuex"

export default {
  name: "Address",
  components: {
    [Field.name]: Field,
    [Button.name]: Button,
    [Switch.name]: Switch,
    [Popup.name]: Popup,
    [Picker.name]: Picker,
  },
  data() {
    return {
      loading: false,
      form: {
        name: "",
        mobile: "",
        country: "",
        countryMark: "",
        city: "",
        countryCode: "",
        zipCode: "",
        detailAddress: "",
        defaultStatus: false,
      },
      countryList: [],
      cityList: [],
      countryCodeList: [
        {
          text: "+86",
          value: "86",
          country: "中国",
          icon: "https://flagcdn.com/w40/cn.png",
        },
        {
          text: "+65",
          value: "65",
          country: "新加坡",
          icon: "https://flagcdn.com/w40/sg.png",
        },
        {
          text: "+60",
          value: "60",
          country: "马来西亚",
          icon: "https://flagcdn.com/w40/my.png",
        },
        {
          text: "+66",
          value: "66",
          country: "泰国",
          icon: "https://flagcdn.com/w40/th.png",
        },
        {
          text: "+62",
          value: "62",
          country: "印尼",
          icon: "https://flagcdn.com/w40/id.png",
        },
        {
          text: "+63",
          value: "63",
          country: "菲律宾",
          icon: "https://flagcdn.com/w40/ph.png",
        },
        {
          text: "+84",
          value: "84",
          country: "越南",
          icon: "https://flagcdn.com/w40/vn.png",
        },
        {
          text: "+95",
          value: "95",
          country: "缅甸",
          icon: "https://flagcdn.com/w40/mm.png",
        },
        {
          text: "+855",
          value: "855",
          country: "柬埔寨",
          icon: "https://flagcdn.com/w40/kh.png",
        },
        {
          text: "+673",
          value: "673",
          country: "文莱",
          icon: "https://flagcdn.com/w40/bn.png",
        },
        {
          text: "+886",
          value: "886",
          country: "台湾",
          icon: "https://flagcdn.com/w40/tw.png",
        },
        {
          text: "+852",
          value: "852",
          country: "香港",
          icon: "https://flagcdn.com/w40/hk.png",
        },
      ],
      showCountryPopup: false,
      showCityPopup: false,
      showCountryCodePopup: false,
    }
  },
  computed: {
    isEdit() {
      return !!this.$route.query.id
    },
    ...mapGetters({
      isZh: "lang/isZh",
      isLoggedIn: "auth/isLoggedIn",
    }),
  },
  created() {
    this.getCountryList()
    if (this.isEdit) {
      this.loadAddressData()
    }
  },
  methods: {
    handleCountry() {
      this.showCountryPopup = true
    },
    // 加载地址数据（编辑模式）
    async loadAddressData() {
      try {
        const response = await getAddressDetail({ id: this.$route.query.id })

        let data = response.data || {}
        const match = data.mobile.match(/^\+(\d+)\s*(.+)$/)

        if (match) {
          const countryCode = "+" + match[1] // 完整区号: +65
          const phoneNumber = match[2] // 电话号码: 17722222222
          data.countryCode = countryCode
          data.mobile = phoneNumber
        }

        this.form = data
      } catch (error) {
        console.error("获取地址失败:", error)
      }
    },
    async getCountryList() {
      try {
        const response = await getAddressCountryList()
        console.log("response :>> ", response)
        const data = response.data
        this.countryList = data.map((item) => {
          // const name = this.isZh ? item.name : item.nameEn
          // if (name === this.form.country) {
          //   this.form.countryMark = item.mark
          // }
          return {
            text: this.isZh ? item.name : item.nameEn,
            value: item.mark,
          }
        })
      } catch (error) {}
    },
    async getCityList(country) {
      try {
        const response = await getCityList({ country })
        const data = response.data
        this.cityList = data.map((item) => {
          return {
            text: this.isZh ? item.name : item.nameEn,
            value: this.isZh ? item.name : item.nameEn,
          }
        })
      } catch (error) {}
    },
    onCountryConfirm(value) {
      if (value.text !== this.form.country) {
        this.form.city = ""
      }
      this.form.country = value.text

      this.$set(this.form, "countryMark", value.value)

      this.showCountryPopup = false
    },
    onCityConfirm(value) {
      this.form.city = value.text
      this.showCityPopup = false
    },
    onCountryCodeConfirm(value) {
      this.$set(this.form, "countryCode", value.text)
      this.showCountryCodePopup = false
    },
    // 保存地址
    async handleSave() {
      if (!this.validateForm()) {
        return
      }
      try {
        if (this.isEdit) {
          await updateAddress({
            id: this.$route.query.id,
            ...this.form,
            mobile: this.form.countryCode + " " + this.form.mobile,
          })
          Toast.success(this.$t("address.updateSuccess"))
        } else {
          await addAddress(this.form)
          Toast.success(this.$t("address.addSuccess"))
        }

        // 返回上一页
        setTimeout(() => {
          this.$router.go(-1)
        }, 1000)
      } catch (error) {
        console.error("保存地址失败:", error)
      }
    },

    // 删除地址
    async handleDelete() {
      try {
        await Dialog.confirm({
          title: this.$t("address.deleteConfirm"),
          message: this.$t("address.deleteMessage"),
        })

        await deleteAddress({ id: this.$route.query.id })
        Toast.success(this.$t("address.deleteSuccess"))

        setTimeout(() => {
          this.$router.go(-1)
        }, 1000)
      } catch (error) {
        if (error !== "cancel") {
          console.error("删除地址失败:", error)
          Toast(this.$t("address.deleteError"))
        }
      }
    },

    // 表单验证
    validateForm() {
      if (!this.form.name) {
        Toast(this.$t("address.namePlaceholder"))
        return false
      }
      if (!this.form.countryCode) {
        Toast(this.$t("address.countryCodeRequired"))
        return false
      }
      if (!this.form.mobile) {
        Toast(this.$t("address.phonePlaceholder"))
        return false
      }
      if (!this.form.country) {
        Toast(this.$t("address.countryPlaceholder"))
        return false
      }
      if (!this.form.city) {
        Toast(this.$t("address.cityPlaceholder"))
        return false
      }
      if (!this.form.zipCode) {
        Toast(this.$t("address.zipCodePlaceholder"))
        return false
      }
      if (!this.form.detailAddress) {
        Toast(this.$t("address.detailAddressPlaceholder"))
        return false
      }
      return true
    },
  },
  watch: {
    isLoggedIn: {
      handler(newVal) {
        if (newVal && this.$route.path === "/address") {
          this.loadAddressData()
        }
      },
      deep: true,
      immediate: false,
    },
    "form.countryMark": {
      handler(newVal) {
        newVal && this.getCityList(newVal)
      },
    },
  },
}
</script>

<style scoped>
/* 自定义样式 */
.van-field__label {
  color: #333;
  font-weight: 500;
}

.van-field__control {
  color: #666;
}

.van-field__control::placeholder {
  color: #ccc;
}

/deep/.van-cell {
  background-color: transparent;
}
/deep/.van-field__control {
  text-align: right;
}
</style>
